/* eslint-disable react-hooks/refs -- diagnostic-only ref read + write
 * inside this module-level helper. Gated behind RENDER_DEBUG; never
 * participates in rendering output. */
import { useRef } from "react";
import { RENDER_DEBUG } from "./debug-flags";

/**
 * Render-time diagnostic for hooks that return a Map. Logs whenever the
 * outer Map identity changes, with a per-entry breakdown so we can tell
 * "outer Map churned but every entry is reference-stable" (cheap churn,
 * no real change) apart from "X entries actually changed" (real update).
 *
 * Usage:
 *
 *   const merged = useMemo(() => mergeRenderUserMap(...), [...]);
 *   useMapChurnLog("renderUserMap", merged);
 *   return [merged, status];
 *
 * Inert when `RENDER_DEBUG` is off (one ref read/write per render).
 *
 * Output format:
 *
 *   [hook:<label>] OUTER-CHURN size=N stable=X changed=Y added=Z removed=W
 *
 * - `stable`: entries whose value reference is `===` to the previous run
 * - `changed`: entries with a different value reference for the same id
 * - `added`: entries that didn't exist in the previous Map
 * - `removed`: entries that existed before but are gone now
 *
 * `stable=N` with `changed=0`/`added=0`/`removed=0` is the "wasted churn"
 * signal — the outer Map allocated fresh but nothing actually changed.
 */
export function useMapChurnLog<K, V>(
  label: string,
  next: ReadonlyMap<K, V>,
): void {
  const prevRef = useRef<ReadonlyMap<K, V> | null>(null);
  if (!RENDER_DEBUG) {
    prevRef.current = next;
    return;
  }
  const prev = prevRef.current;
  if (prev !== next) {
    let stableEntries = 0;
    let changedEntries = 0;
    let addedEntries = 0;
    let removedEntries = 0;
    if (prev) {
      for (const [id, val] of next) {
        const prevVal = prev.get(id);
        if (prevVal === undefined) addedEntries += 1;
        else if (prevVal === val) stableEntries += 1;
        else changedEntries += 1;
      }
      for (const id of prev.keys()) {
        if (!next.has(id)) removedEntries += 1;
      }
    }
    console.debug(
      `[hook:${label}] OUTER-CHURN size=${next.size}`,
      `stable=${stableEntries} changed=${changedEntries} added=${addedEntries} removed=${removedEntries}`,
    );
  }
  prevRef.current = next;
}
